import React, { useState } from "react";
import { ArrowUpRight, CalendarCheck } from "lucide-react";
import { useLang, UI } from "../i18n";
import { PROJECTS, SELECTION, THEMES, FICHE, TIER_LABEL, IMG, SHERBROOKE, LEGACY } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { PageHero } from "../components/PageHero";
import { MiniSite, MonoTag } from "../components/motifs";
import { FaqBlock } from "../components/Fiche";
import { WhatsAppShare } from "../components/Lightbox";
import { ContactSection } from "../components/Layout";

const teamOffer = (d, slug) => ({
  slug, name: d.name, code: d.code, theme: d.theme, tier: 2,
  tag: d.league, ages: d.fiche.ages, price: d.fiche.price, duration: d.fiche.duration,
});
const OFFERS = [...PROJECTS, teamOffer(SHERBROOKE, "sherbrooke"), teamOffer(LEGACY, "legacy-3r")];
const TRIPS = PROJECTS.filter((p) => p.destinations).flatMap((p) =>
  p.destinations.filter((d) => !d.tbd).map((d) => ({ program: p.name, place: d.place, period: d.period }))
);

// Portail de candidature : sélecteur visuel de toutes les offres
export default function Candidature() {
  useLenis();
  const { t } = useLang();
  const [sel, setSel] = useState(OFFERS[0]);
  return (
    <MiniSite theme={THEMES.base}>
      <PageHero
        code="AST · CANDIDATURE"
        overline={t(UI.nav.candidature)}
        title={t({ fr: "Choisissez votre aventure", en: "Choose your adventure", es: "Elige tu aventura" })}
        subtitle={t({ fr: "Sélectionnez l'offre qui vous correspond : sport-études, immersion 7 jours, road trip, Winter Cup ou tryout. Chaque admission passe par un entretien de sélection (motivation + dossier scolaire).", en: "Pick the offer that fits you: sport-study, 7-day immersion, road trip, Winter Cup or tryout. Every admission goes through a selection interview (motivation + academic record).", es: "Elige la oferta que te corresponde. Cada admisión pasa por una entrevista de selección (motivación + expediente escolar)." })}
        image={IMG.coach}
      />

      {/* PHILOSOPHIE DE SÉLECTION */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-12">
        <Reveal>
          <div data-testid="selection-philosophy" className="rounded-md border border-white/10 p-8"
            style={{ backgroundColor: "var(--ms-surface)", borderTop: "4px solid #F6B02C" }}>
            <MonoTag style={{ color: "#F6B02C" }}>{t(SELECTION.title)}</MonoTag>
            <p className="font-heading text-lg md:text-xl font-semibold mt-4 italic">{t(SELECTION.quote)}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-2 mt-5">
              {SELECTION.items.map((it, i) => (
                <p key={i} className="font-body text-sm text-white/75">• {t(it)}</p>
              ))}
            </div>
          </div>
        </Reveal>
      </section>

      {/* SÉLECTEUR D'OFFRES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="offer-selector">
          {OFFERS.map((p) => (
            <button key={p.slug} type="button" onClick={() => setSel(p)} data-testid={`offer-${p.slug}`}
              className={`text-left rounded-md border p-5 transition-colors duration-200 ${sel.slug === p.slug ? "border-white/60" : "border-white/10 hover:border-white/30"}`}
              style={{ backgroundColor: p.theme.surface }}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.theme.accent }} />
                <MonoTag className="text-white/50">{p.code}</MonoTag>
              </div>
              <p className="font-heading font-semibold mt-2">{p.name}</p>
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/50 mt-1">{t(p.ages)}</p>
            </button>
          ))}
        </div>
        <div className="lg:col-span-5">
          <Reveal key={sel.slug}>
            <div data-testid="offer-recap" className="rounded-md border border-white/15 p-8 sticky top-28"
              style={{ backgroundColor: sel.theme.surface, borderTop: `4px solid ${sel.theme.accent}` }}>
              <MonoTag style={{ color: sel.theme.accent }}>{t(sel.tag)}</MonoTag>
              <h2 className="font-display text-3xl uppercase mt-3">{sel.name}</h2>
              <ul className="mt-5 space-y-2 font-body text-sm text-white/85">
                <li><strong>{t(FICHE.age)} :</strong> {t(sel.ages)}</li>
                <li><strong>{t(FICHE.price)} :</strong> {t(sel.price || FICHE.onRequest)}</li>
                <li><strong>{t(FICHE.duration)} :</strong> {t(sel.duration)}</li>
                <li><strong>{t(FICHE.tier)} :</strong> {t(TIER_LABEL[sel.tier])}</li>
                <li className="blink" style={{ color: sel.theme.accent }}>
                  ● {t({ fr: "Places strictement limitées — sessions régulièrement complètes", en: "Strictly limited spots — sessions regularly sell out", es: "Plazas estrictamente limitadas" })}
                </li>
                <li className="text-white/60">
                  {t({ fr: "Sélection sur entretien de motivation + dossier scolaire · paiement le 5 ou le 15 du mois", en: "Selection via motivation interview + academic record · payment on the 5th or 15th", es: "Selección por entrevista + expediente escolar · pago el 5 o el 15" })}
                </li>
              </ul>
              <div className="flex flex-wrap gap-3 mt-7">
                <a href="#contact" data-testid="offer-apply"
                  className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-heading font-semibold uppercase text-xs tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                  style={{ backgroundColor: sel.theme.accent }}>
                  <CalendarCheck size={15} /> {t({ fr: "Planifier un entretien de sélection", en: "Schedule a selection interview", es: "Agendar una entrevista" })}
                </a>
                <WhatsAppShare testid="offer-whatsapp" text={`${sel.name} — Airsports Travel`}
                  label={t({ fr: "Échanger sur WhatsApp", en: "Chat on WhatsApp", es: "Hablar por WhatsApp" })} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20">
        <FaqBlock accent="#F6B02C" />
      </section>

      <ContactSection defaultProgram={sel.name} accent={sel.theme.accent} programs={PROJECTS.map((p) => p.name)} trips={TRIPS} />
    </MiniSite>
  );
}
